import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  isSuperAdmin,
  isBrandAdmin,
  isBranchAdmin
} from '../utils/portalRoles';

const idOf = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    return String(value.id || value._id || '');
  }
  return String(value);
};

/**
 * Portal tenancy helpers for admin UI:
 * - Super Admin: create anything, pick any brand/branch
 * - Brand Admin: cannot create brands; create only under their brand
 * - Branch Admin: cannot create brands/branches; create only under their branch
 */
export default function usePortalScope() {
  const { user, portalContext } = useAuth();

  return useMemo(() => {
    const role = user?.role;
    const brandId = idOf(user?.brandId) || idOf(user?.brand);
    const salonId = idOf(user?.salonId) || idOf(user?.salon);
    const superAdmin = isSuperAdmin(role);
    const brandAdmin = isBrandAdmin(role);
    const branchAdmin = isBranchAdmin(role);

    return {
      role,
      user,
      brandId: brandId || null,
      salonId: salonId || null,
      brandName: user?.brand?.name || null,
      salonName: user?.salon?.name || user?.salon?.branch || null,
      isSuperAdmin: superAdmin,
      isBrandAdmin: brandAdmin,
      isBranchAdmin: branchAdmin,
      lockBrandId: superAdmin ? null : brandId || null,
      lockSalonId: branchAdmin ? salonId || null : null,
      canCreateBrand: superAdmin,
      canDeleteBrand: superAdmin,
      canCreateBranch: superAdmin || brandAdmin,
      canDeleteBranch: superAdmin || brandAdmin,
      canManageAdmins: superAdmin,
      canManageBranchAdmins: superAdmin || brandAdmin,
      showBrandPicker: superAdmin,
      showBranchPicker: superAdmin || brandAdmin,
      brands: portalContext?.brands || [],
      salons: portalContext?.salons || []
    };
  }, [user, portalContext]);
}
